import type { BlockEmbed } from 'quill/blots/block';
import React from 'react';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarSpanButtonGroup from './buttons/ToolbarSpanButtonGroup';
import DeleteMenuItem from './DeleteMenuItem';
import FloatingToolbar from './FloatingToolbar';
import formatBlotWithoutScrolling from './formatBlotWithoutScrolling';
import Toolbar, { ToolbarOverflowMenu } from './Toolbar';
import useBlotHighlightedClass from './useBlotHighlightedClass';
import { MenuSeparator } from '../../../components/Menu';
import newTabIcon from '../../../icons/non-prefilled/16x16/newTab.svg';
import useIsPublicView from '../../../utils/useIsPublicView';
import Video from '../../formats/Video';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import { getFullUrl } from '../../utils/matchVideo';
import ConvertIntoLinkMenuItem from '../ConvertIntoLinkMenuItem';
import CopyLinkMenuItem from '../CopyLinkMenuItem';
import OpenInNewTabMenuItem from '../OpenInNewTabMenuItem';

type Formats = Record<string, string[] | string | null | undefined> & {
  comment?: string[] | null;
};

interface VideoToolbarOverflowMenuProps {
  blot: BlockEmbed;
  link: string;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const VideoToolbarOverflowMenu = ({
  blot,
  link,
}: VideoToolbarOverflowMenuProps) => {
  const { quill } = useQuill();

  return (
    <>
      <OpenInNewTabMenuItem link={link} />
      {quill && (
        <ConvertIntoLinkMenuItem
          quill={quill}
          blot={blot}
          link={getFullUrl(link)}
        />
      )}
      <CopyLinkMenuItem link={link} />
      <MenuSeparator />
      <DeleteMenuItem onClick={() => blot.remove()}>
        Delete video
      </DeleteMenuItem>
    </>
  );
};

interface BaseVideoToolbarProps {
  hoveredBlot: BlockEmbed;
  formats: Formats;
  format: (format: { height?: string | null; span?: string | null }) => void;
  setPersist: (persist: boolean) => void;
  value: string;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseVideoToolbar = ({
  hoveredBlot,
  formats,
  format,
  setPersist,
  value,
}: BaseVideoToolbarProps) => {
  const { readOnly } = useQuill();
  const isPublicView = useIsPublicView();
  useBlotHighlightedClass(hoveredBlot);

  if (readOnly && isPublicView) {
    return (
      <Toolbar>
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={newTabIcon}
            tooltip="Open in new tab"
            to={value}
            newTab
          >
            Open
          </ToolbarButton>
        </ToolbarButtonGroup>
      </Toolbar>
    );
  }

  return (
    <Toolbar
      targetBlot={hoveredBlot}
      overflowMenu={
        readOnly ? undefined : (
          <ToolbarOverflowMenu onMenuOpenChange={setPersist}>
            <VideoToolbarOverflowMenu blot={hoveredBlot} link={value} />
          </ToolbarOverflowMenu>
        )
      }
    >
      {readOnly && (
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={newTabIcon}
            tooltip="Open in new tab"
            to={value}
            newTab
          >
            Open
          </ToolbarButton>
        </ToolbarButtonGroup>
      )}
      {!readOnly && (
        <ToolbarSpanButtonGroup
          span={formats.span}
          onSpanChange={(newSpan) => format({ span: newSpan })}
        />
      )}
      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
    </Toolbar>
  );
};

type Context = {
  formats: Formats;
  value: string;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  isHoveredBlot(blot) {
    return blot.statics.blotName === Video.blotName;
  },
  getContext(blot: Video) {
    return {
      formats: blot.formats(),
      value: blot.value().video,
    };
  },
};

const VideoToolbar = () => {
  const { quill } = useQuill();

  const {
    bounds,
    hoveredBlot,
    context: { formats, value } = { formats: undefined, value: undefined },
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  return (
    <FloatingToolbar.Toolbar bounds={bounds}>
      {hoveredBlot instanceof Video && formats && value && (
        <BaseVideoToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          format={(attributes) => {
            if (!quill) return;

            formatBlotWithoutScrolling({
              quill,
              attributes,
              blot: hoveredBlot,
            });
          }}
          setPersist={setPersist}
          value={value}
        />
      )}
    </FloatingToolbar.Toolbar>
  );
};

export default VideoToolbar;
